import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Check, Download, FileSearch, Loader2, Mail, Plus, X } from "lucide-react";
import { API, SEVERITY_META, blobErrorDetail, downloadBlob } from "./reviewMeta";

const EMPTY_CUSTOM = { severity: "amber", issue: "", anchor_snippet: "", suggested_comment: "", proposed_clause: "" };

function CustomCommentForm({ onSave, onCancel }) {
  const [draft, setDraft] = useState(EMPTY_CUSTOM);
  const save = () => {
    if (!draft.issue.trim()) return toast.error("Add a short title for the comment.");
    if (!draft.suggested_comment.trim()) return toast.error("Add the comment text.");
    onSave(draft);
  };
  const field = "w-full bg-white border border-stone-300 rounded-lg px-3 py-2 text-sm text-slate-900 placeholder:text-stone-400 focus:outline-none focus:border-[#3EA8DC] focus:ring-2 focus:ring-[#3EA8DC]/20 transition-all";
  return (
    <div className="bg-white border-2 border-[#1B4F8A]/40 rounded-xl p-5 shadow-sm" data-testid="contract-review-custom-form">
      <div className="flex items-center justify-between mb-4">
        <p className="text-[10px] font-mono uppercase tracking-[0.14em] text-[#1B4F8A]">New custom comment</p>
        <button type="button" onClick={onCancel} aria-label="Cancel" data-testid="contract-review-custom-cancel-btn" className="text-stone-400 hover:text-slate-900">
          <X className="w-4 h-4" />
        </button>
      </div>
      <div className="space-y-4">
        <div>
          <label className="text-[10px] font-mono tracking-[0.14em] uppercase text-stone-500 mb-2 block">Severity</label>
          <div className="flex gap-2">
            {["red", "amber", "green"].map((sev) => {
              const sm = SEVERITY_META[sev];
              const active = draft.severity === sev;
              return (
                <button key={sev} type="button" onClick={() => setDraft((d) => ({ ...d, severity: sev }))} data-testid={`contract-review-custom-severity-${sev}`} aria-pressed={active}
                  className={`inline-flex items-center gap-2 rounded-md px-3 py-1.5 text-xs font-mono uppercase tracking-[0.1em] border transition-all ${active ? `${sm.border} ${sm.bg} ${sm.text}` : "border-stone-300 text-stone-500 hover:border-stone-400"}`}>
                  <span className={`w-2 h-2 rounded-full ${sm.dot}`} />
                  {sm.label}
                </button>
              );
            })}
          </div>
        </div>
        <div>
          <label className="text-[10px] font-mono tracking-[0.14em] uppercase text-stone-500 mb-2 block">Short title <span className="text-rose-600">*</span></label>
          <input type="text" value={draft.issue} onChange={(e) => setDraft((d) => ({ ...d, issue: e.target.value }))} placeholder="e.g. Missing late-payment interest cap" data-testid="contract-review-custom-issue-input" className={field} />
        </div>
        <div>
          <label className="text-[10px] font-mono tracking-[0.14em] uppercase text-stone-500 mb-2 block">Quote from contract (anchor, optional)</label>
          <textarea value={draft.anchor_snippet} onChange={(e) => setDraft((d) => ({ ...d, anchor_snippet: e.target.value }))} rows={2} placeholder={draft.proposed_clause.trim() ? "Paste the verbatim last sentence of the clause the new clause should follow. Leave blank to place it in the proposed clauses section before the signature block." : "Paste the verbatim sentence from the contract you want to comment on. Leave blank to attach the comment to the contract title."} data-testid="contract-review-custom-anchor-input" className={`${field} italic resize-y`} />
        </div>
        <div>
          <label className="text-[10px] font-mono tracking-[0.14em] uppercase text-stone-500 mb-2 block">Comment text <span className="text-rose-600">*</span></label>
          <textarea value={draft.suggested_comment} onChange={(e) => setDraft((d) => ({ ...d, suggested_comment: e.target.value }))} rows={4} placeholder="The comment that will be inserted into Word at the quoted location." data-testid="contract-review-custom-comment-input" className={`${field} resize-y`} />
        </div>
        <div>
          <label className="text-[10px] font-mono tracking-[0.14em] uppercase text-stone-500 mb-2 block">Proposed clause to insert (optional, tracked change)</label>
          <textarea value={draft.proposed_clause} onChange={(e) => setDraft((d) => ({ ...d, proposed_clause: e.target.value }))} rows={3} placeholder="Full clause wording, numbered to fit (for example 5A. ...). It is inserted into the contract as a tracked change after the quoted text above." data-testid="contract-review-custom-clause-input" className={`${field} resize-y`} />
        </div>
        <div className="flex items-center justify-end gap-2 pt-2">
          <button type="button" onClick={onCancel} data-testid="contract-review-custom-cancel-action" className="text-xs font-mono tracking-[0.1em] uppercase text-stone-500 hover:text-slate-900 px-4 py-2">Cancel</button>
          <button type="button" onClick={save} data-testid="contract-review-custom-save-btn" className="inline-flex items-center gap-2 bg-[#1B4F8A] hover:bg-[#163F70] text-white font-semibold px-4 py-2 rounded-lg text-sm shadow-md shadow-[#1B4F8A]/20">
            <Plus className="w-4 h-4" /> Add to review
          </button>
        </div>
      </div>
    </div>
  );
}

function FindingCard({ f, i, readOnly, busy, onToggle, onEdit, onEditClause, onRemove }) {
  const m = SEVERITY_META[f.severity] || SEVERITY_META.amber;
  const Icon = m.icon;
  const included = f._include;
  const hasClause = !!(f.proposed_clause || f._clause || f.category === "missing_from_contract");
  return (
    <div className={`rounded-xl border ${included ? m.border : "border-stone-200"} ${included ? m.bg : "bg-stone-50"} transition-all ${included ? "" : "opacity-60"}`} data-testid={`contract-review-finding-${i}`}>
      <div className="px-4 py-3 flex items-start gap-3">
        <button type="button" onClick={() => onToggle(i)} disabled={readOnly} data-testid={`contract-review-include-toggle-${i}`} aria-pressed={included} aria-label={included ? "Exclude this comment" : "Include this comment"}
          className={`mt-0.5 w-5 h-5 shrink-0 rounded-md border-2 flex items-center justify-center transition-all ${included ? "bg-[#1B4F8A] border-[#1B4F8A]" : "bg-white border-stone-300 hover:border-stone-500"} disabled:cursor-default`}>
          {included && <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} />}
        </button>
        <Icon className={`w-4 h-4 mt-1 shrink-0 ${m.text}`} aria-hidden />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-sm font-medium text-slate-900">
              <span className="text-stone-400 font-mono text-xs mr-1.5">#{i + 1}</span>
              {f.issue}
            </p>
            {f.category === "correspondence" && (
              <span className="inline-flex items-center gap-1 text-[9px] font-mono uppercase tracking-[0.14em] text-[#1B4F8A] bg-[#3EA8DC]/10 border border-[#3EA8DC]/40 rounded px-1.5 py-0.5" data-testid={`contract-review-correspondence-badge-${i}`}>
                <Mail className="w-2.5 h-2.5" /> From emails
              </span>
            )}
            {f.category === "missing_from_contract" && (
              <span className="inline-flex items-center gap-1 text-[9px] font-mono uppercase tracking-[0.14em] text-[#7c3aed] bg-violet-50 border border-violet-300 rounded px-1.5 py-0.5" data-testid={`contract-review-missing-badge-${i}`}>
                <FileSearch className="w-2.5 h-2.5" /> Missing from contract
              </span>
            )}
            {f._isCustom && (
              <span className="text-[9px] font-mono uppercase tracking-[0.14em] text-[#1B4F8A] border border-[#1B4F8A]/40 rounded px-1.5 py-0.5" data-testid={`contract-review-custom-badge-${i}`}>Custom</span>
            )}
            {readOnly && (
              <span className={`text-[9px] font-mono uppercase tracking-[0.14em] rounded px-1.5 py-0.5 border ${included ? "text-emerald-700 border-emerald-300 bg-emerald-50" : "text-stone-500 border-stone-300"}`} data-testid={`contract-review-decision-${i}`}>
                {f._decided ? (included ? "Included" : "Excluded") : "Not decided"}
              </span>
            )}
          </div>
          <p className="text-[11px] text-stone-500 font-mono mt-0.5">
            {m.label} · {f.category}{f.section_hint ? ` · ${f.section_hint}` : ""}
          </p>
        </div>
        {f._isCustom && !readOnly && (
          <button type="button" onClick={() => onRemove(i)} aria-label="Delete custom comment" data-testid={`contract-review-remove-${i}`} className="shrink-0 text-stone-400 hover:text-rose-500 p-1">
            <X className="w-4 h-4" />
          </button>
        )}
      </div>
      <div className="px-4 pb-4 pl-12 space-y-3 text-sm text-slate-700">
        {f.rationale && <p className="leading-relaxed">{f.rationale}</p>}
        {f.anchor_snippet && (
          <blockquote className="border-l-2 border-stone-300 pl-3 text-[12px] italic text-stone-500">&ldquo;{f.anchor_snippet}&rdquo;</blockquote>
        )}
        <div className="bg-white border border-stone-200 rounded-lg p-3 mt-2">
          <div className="flex items-center justify-between mb-2">
            <p className="text-[10px] font-mono uppercase tracking-[0.14em] text-stone-500">{hasClause ? "Comment explaining the addition" : "Comment text to insert"}</p>
            {!readOnly && <p className="text-[10px] text-stone-500">editable</p>}
          </div>
          <textarea value={f._edited ?? ""} onChange={(e) => onEdit(i, e.target.value)} disabled={readOnly || !included || busy}
            rows={Math.min(8, Math.max(2, ((f._edited || "").match(/\n/g) || []).length + 2))}
            data-testid={`contract-review-comment-editor-${i}`}
            className="w-full bg-transparent text-sm text-slate-800 leading-relaxed resize-y focus:outline-none disabled:cursor-not-allowed whitespace-pre-wrap"
            placeholder="(Comment text, leave blank to skip)" />
        </div>
        {hasClause && (
          <div className="bg-[#1B4F8A]/5 border border-[#1B4F8A]/30 rounded-lg p-3 mt-2" data-testid={`contract-review-clause-box-${i}`}>
            <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
              <p className="text-[10px] font-mono uppercase tracking-[0.14em] text-[#1B4F8A]">Proposed clause, inserted as a tracked change</p>
              <p className="text-[10px] text-stone-500" data-testid={`contract-review-clause-placement-${i}`}>
                {f.insert_after_snippet ? `after: “${f.insert_after_snippet.slice(0, 70)}${f.insert_after_snippet.length > 70 ? "…" : ""}”` : "no related clause found: goes to the proposed clauses section before the signature block"}
              </p>
            </div>
            <textarea value={f._clause ?? ""} onChange={(e) => onEditClause(i, e.target.value)} disabled={readOnly || !included || busy}
              rows={Math.min(10, Math.max(3, Math.ceil((f._clause || "").length / 110) + 1))}
              data-testid={`contract-review-clause-editor-${i}`}
              className="w-full bg-transparent text-sm text-slate-800 leading-relaxed resize-y focus:outline-none disabled:cursor-not-allowed whitespace-pre-wrap"
              placeholder="(Clause wording, leave blank to only attach the comment to the title)" />
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Step 3: review the verified findings, reword, include or exclude,
 * then build the annotated DOCX. In ``readOnly`` mode (history) it
 * shows the recorded decisions and offers the built file.
 */
export default function FindingsEditor({ job, reviewable, setReviewable, summary, setSummary, readOnly = false, onBuilt }) {
  const [building, setBuilding] = useState(false);
  const [showCustom, setShowCustom] = useState(false);
  const [lastReport, setLastReport] = useState(job.report || null);
  const includedCount = reviewable.filter((f) => f._include).length;

  const toggle = (i) => setReviewable((arr) => arr.map((f, idx) => (idx === i ? { ...f, _include: !f._include } : f)));
  const edit = (i, v) => setReviewable((arr) => arr.map((f, idx) => (idx === i ? { ...f, _edited: v } : f)));
  const editClause = (i, v) => setReviewable((arr) => arr.map((f, idx) => (idx === i ? { ...f, _clause: v } : f)));
  const setAll = (v) => setReviewable((arr) => arr.map((f) => ({ ...f, _include: v })));
  const remove = (i) => setReviewable((arr) => arr.filter((_, idx) => idx !== i));
  const addCustom = (d) => {
    const clause = (d.proposed_clause || "").trim();
    setReviewable((arr) => [...arr, {
      severity: d.severity, category: "other", issue: d.issue.trim(), rationale: "",
      anchor_snippet: clause ? "" : d.anchor_snippet.trim(), insert_after_snippet: clause ? d.anchor_snippet.trim() : "",
      suggested_comment: d.suggested_comment.trim(), proposed_clause: clause, section_hint: "",
      _include: true, _edited: d.suggested_comment.trim(), _clause: clause, _isCustom: true,
    }]);
    setSummary((s) => (s ? { ...s, total: s.total + 1, [d.severity]: (s[d.severity] || 0) + 1 } : s));
    setShowCustom(false);
    toast.success(clause ? "Custom clause added, it will be inserted as a tracked change." : "Custom comment added.");
  };

  const build = async () => {
    const included = reviewable.filter((f) => f._include);
    if (!included.length) return toast.error("Select at least one comment to include.");
    setBuilding(true);
    try {
      const payload = {
        findings: included.map((f) => ({
          severity: f.severity || "amber", category: f.category || "other", issue: f.issue || "", rationale: f.rationale || "",
          anchor_snippet: f.anchor_snippet || "", suggested_comment: (f._edited ?? f.suggested_comment ?? "").trim(), section_hint: f.section_hint || "",
          proposed_clause: (f._clause ?? f.proposed_clause ?? "").trim(), insert_after_snippet: f.insert_after_snippet || "",
        })),
        decisions: reviewable.map((f) => ({
          issue: f.issue || "", severity: f.severity || "amber", category: f.category || "other", included: !!f._include,
          comment: (f._edited ?? f.suggested_comment ?? "").trim(), clause: (f._clause ?? f.proposed_clause ?? "").trim(), custom: !!f._isCustom,
        })),
      };
      const res = await downloadBlob(`${API}/api/proposals/contract-review/${job.job_id}/build`, `contract-review-${job.job_id}.docx`, { method: "POST", data: payload });
      const n = (h) => parseInt(res.headers[h] || "0", 10);
      const report = { placed: n("x-findings-placed"), inserted: n("x-findings-inserted"), fallback: n("x-findings-fallback"), general: n("x-findings-general"), total: included.length };
      setLastReport(report);
      toast.success(
        `Built: ${report.placed} comment${report.placed === 1 ? "" : "s"} on clauses, ${report.inserted} clause${report.inserted === 1 ? "" : "s"} inserted in place, ${report.fallback} in the proposed clauses section, ${report.general} on the title.`,
      );
      onBuilt?.();
    } catch (err) {
      toast.error(await blobErrorDetail(err, "Build failed."));
    } finally {
      setBuilding(false);
    }
  };

  const downloadBuilt = async () => {
    try {
      await downloadBlob(`${API}/api/proposals/contract-review/${job.job_id}/download`, `contract-review-${job.job_id}.docx`);
    } catch (err) {
      toast.error(await blobErrorDetail(err, "Download failed."));
    }
  };

  return (
    <div className="bg-white border border-stone-200 rounded-2xl p-6 sm:p-8 shadow-sm" data-testid="contract-review-result">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-5">
        <div>
          <p className="text-[10px] font-mono tracking-[0.14em] uppercase text-stone-500 mb-1">{readOnly ? "Recorded decisions" : "Review and approve"}</p>
          <h2 className="text-xl text-slate-900">{job.project_reference || "Contract review"}</h2>
          <p className="text-xs text-stone-500 mt-1">
            <span className="text-slate-700 font-semibold" data-testid="contract-review-included-count">{includedCount}</span> of {reviewable.length} comment{reviewable.length === 1 ? "" : "s"} {readOnly ? "included" : "selected"}
            {lastReport ? ` · last build: ${lastReport.placed} on clauses · ${lastReport.inserted} inserted · ${lastReport.fallback} in proposed section · ${lastReport.general} on title` : ""}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {!readOnly && (
            <>
              <button type="button" onClick={() => setAll(true)} data-testid="contract-review-select-all-btn" className="text-xs font-mono tracking-[0.1em] uppercase text-stone-500 hover:text-slate-900 border border-stone-300 rounded-md px-3 py-2">Select all</button>
              <button type="button" onClick={() => setAll(false)} data-testid="contract-review-deselect-all-btn" className="text-xs font-mono tracking-[0.1em] uppercase text-stone-500 hover:text-slate-900 border border-stone-300 rounded-md px-3 py-2">Deselect all</button>
              <button type="button" onClick={build} disabled={building || includedCount === 0} data-testid="contract-review-build-btn" className="inline-flex items-center gap-2 bg-[#1B4F8A] hover:bg-[#163F70] disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold px-5 py-2.5 rounded-lg shadow-md shadow-[#1B4F8A]/20">
                {building ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                {building ? "Building…" : "Build & download DOCX"}
              </button>
            </>
          )}
          {readOnly && job.has_docx && (
            <button type="button" onClick={downloadBuilt} data-testid="contract-review-download-built-btn" className="inline-flex items-center gap-2 bg-[#1B4F8A] hover:bg-[#163F70] text-white font-semibold px-5 py-2.5 rounded-lg shadow-md shadow-[#1B4F8A]/20">
              <Download className="w-4 h-4" /> Download annotated DOCX
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3 mb-6">
        {["red", "amber", "green"].map((sev) => {
          const m = SEVERITY_META[sev];
          return (
            <div key={sev} data-testid={`contract-review-summary-${sev}`} className={`rounded-xl border ${m.border} ${m.bg} p-3`}>
              <div className="flex items-center gap-2 mb-1">
                <span className={`w-2 h-2 rounded-full ${m.dot}`} />
                <span className="text-[10px] font-mono uppercase tracking-[0.14em] text-slate-600">{m.label}</span>
              </div>
              <p className="text-2xl font-light text-slate-900 tabular-nums">{summary?.[sev] ?? 0}</p>
            </div>
          );
        })}
      </div>

      <div className="space-y-3" data-testid="contract-review-findings-list">
        {reviewable.map((f, i) => (
          <FindingCard key={i} f={f} i={i} readOnly={readOnly} busy={building} onToggle={toggle} onEdit={edit} onEditClause={editClause} onRemove={remove} />
        ))}
      </div>

      {reviewable.length === 0 && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-5 mt-4 text-sm text-emerald-800" data-testid="contract-review-no-findings">
          <p className="font-semibold mb-1">No red lines found</p>
          <p className="text-emerald-700/80">No flags against the proposal, the T&amp;Cs or the correspondence. You can still add custom comments before building the DOCX.</p>
        </div>
      )}

      {!readOnly && (
        <div className="mt-4">
          {!showCustom ? (
            <button type="button" onClick={() => setShowCustom(true)} data-testid="contract-review-add-custom-btn" className="w-full inline-flex items-center justify-center gap-2 border-2 border-dashed border-stone-300 hover:border-[#1B4F8A] hover:bg-[#1B4F8A]/5 text-stone-500 hover:text-[#1B4F8A] rounded-xl px-4 py-3 text-sm font-mono tracking-[0.06em] uppercase transition-colors">
              <Plus className="w-4 h-4" /> Add custom comment
            </button>
          ) : (
            <CustomCommentForm onSave={addCustom} onCancel={() => setShowCustom(false)} />
          )}
        </div>
      )}
    </div>
  );
}
